import React, { useEffect, useState, useMemo } from "react";
import { EmptyState } from "../../components/ui";
import { Icon } from "../shared/components";
import { ensureArray, ensureObject, formatMoney, formatMinutesAsLabel } from "../shared/utils";

// ── Primitives ──────────────────────────────────────────────────────────────────
function KpiCard({ title, value, hint, tone = "default", icon }) {
  return (
    <div className={`reportKpi tone-${tone}`}>
      {icon && <div className="reportKpiIcon"><Icon name={icon} size={16} /></div>}
      <div className="reportKpiBody">
        <span>{title}</span>
        <strong>{value}</strong>
        {hint && <small>{hint}</small>}
      </div>
    </div>
  );
}

function BarRow({ label, value, max, alt, extra }) {
  const pct = max > 0 ? Math.round((Number(value || 0) / max) * 100) : 0;
  return (
    <div className="reportBarRow">
      <div className="reportBarLabel">
        <strong>{label}</strong>
        <span>{extra || value}</span>
      </div>
      <div className={`reportBarTrack${alt ? " alt" : ""}`}>
        <div style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

// Build a complete list of last N days as YYYY-MM-DD
function buildDayLabels(days = 14) {
  const labels = [];
  for (let i = days - 1; i >= 0; i--) {
    const d = new Date();
    d.setDate(d.getDate() - i);
    labels.push(d.toISOString().slice(0, 10));
  }
  return labels;
}

function MiniChart({ data, color = "var(--accent)", days = 14 }) {
  const labels = buildDayLabels(days);
  const map = {};
  for (const row of ensureArray(data)) map[row.day] = Number(row.total || 0);
  const values = labels.map((d) => map[d] || 0);
  const max = Math.max(1, ...values);

  return (
    <div className="reportMiniChart">
      {values.map((v, i) => (
        <div key={labels[i]} className="reportMiniBar" title={`${labels[i]}: ${v}`}>
          <div style={{ height: `${Math.round((v / max) * 100)}%`, background: color }} />
        </div>
      ))}
    </div>
  );
}

function DualChart({ sent, received, days = 14 }) {
  const labels = buildDayLabels(days);
  const sentMap = {};
  const rcvMap = {};
  for (const row of ensureArray(sent)) sentMap[row.day] = Number(row.total || row.sent || 0);
  for (const row of ensureArray(received)) rcvMap[row.day] = Number(row.total || row.received || 0);

  // For messages, the data comes in the same row
  const msgRows = sent; // actually "messages" array from timeline
  const sentVals = labels.map((d) => {
    const row = msgRows.find((r) => r.day === d);
    return row ? Number(row.sent || 0) : 0;
  });
  const rcvVals = labels.map((d) => {
    const row = msgRows.find((r) => r.day === d);
    return row ? Number(row.received || 0) : 0;
  });
  const max = Math.max(1, ...sentVals, ...rcvVals);

  return (
    <div className="reportDualChart">
      {labels.map((d, i) => (
        <div key={d} className="reportDualBar" title={`${d}: enviadas ${sentVals[i]}, recebidas ${rcvVals[i]}`}>
          <div className="dualBarSent" style={{ height: `${Math.round((sentVals[i] / max) * 100)}%` }} />
          <div className="dualBarRcv" style={{ height: `${Math.round((rcvVals[i] / max) * 100)}%` }} />
        </div>
      ))}
    </div>
  );
}

// ── Tab: Geral ─────────────────────────────────────────────────────────────────
function TabGeral({ summary, timeline, loading }) {
  const created = ensureArray(timeline?.created);
  const finished = ensureArray(timeline?.finished);
  const messages = ensureArray(timeline?.messages);

  const totalSent = messages.reduce((s, r) => s + Number(r.sent || 0), 0);
  const totalRcv = messages.reduce((s, r) => s + Number(r.received || 0), 0);

  return (
    <div className="reportTabContent">
      <div className="reportKpiGrid">
        <KpiCard title="Conversas abertas" value={summary?.open_conversations ?? 0} hint={`${summary?.waiting_conversations ?? 0} aguardando resposta`} tone="green" icon="chat" />
        <KpiCard title="Finalizadas" value={summary?.finished_conversations ?? 0} hint="ciclo encerrado" tone="blue" icon="check" />
        <KpiCard title="Contatos" value={summary?.total_contacts ?? 0} hint="base ativa" tone="default" icon="contact" />
        <KpiCard title="SLA crítico" value={summary?.critical_conversations ?? 0} hint={`${summary?.warning_conversations ?? 0} em atenção`} tone="red" icon="warning" />
        <KpiCard title="Mensagens enviadas" value={totalSent} hint={`${totalRcv} recebidas`} tone="purple" icon="send" />
        <KpiCard title="Tempo médio 1ª resposta" value={formatMinutesAsLabel(summary?.avg_first_response_minutes ?? 0)} hint="tempo operacional" tone="amber" icon="clock" />
      </div>

      <div className="reportChartGrid">
        <div className="reportBox">
          <div className="reportBoxHeader">
            <h3>Conversas abertas por dia</h3>
            <span className="reportBoxHint">últimos 14 dias</span>
          </div>
          <MiniChart data={created} color="var(--accent)" days={14} />
          <div className="reportChartLegend">
            <span className="legendDot" style={{ background: "var(--accent)" }} />Abertas
          </div>
        </div>

        <div className="reportBox">
          <div className="reportBoxHeader">
            <h3>Mensagens trocadas</h3>
            <span className="reportBoxHint">últimos 14 dias</span>
          </div>
          <DualChart sent={messages} received={messages} days={14} />
          <div className="reportChartLegend">
            <span className="legendDot" style={{ background: "var(--accent)" }} />Enviadas
            <span className="legendDot" style={{ background: "var(--blue)" }} />Recebidas
          </div>
        </div>

        <div className="reportBox emphasis">
          <h3>Leitura executiva</h3>
          <p>O Safe Zap está acompanhando operação de conversas, evolução no pipeline e carga por atendente.</p>
          <div className="reportInsightList">
            <div className="reportInsightRow"><span>Conversas abertas</span><strong>{summary?.open_conversations ?? 0}</strong></div>
            <div className="reportInsightRow"><span>Leads ganhos</span><strong>{summary?.won_leads ?? 0}</strong></div>
            <div className="reportInsightRow"><span>Receita gerada</span><strong>{formatMoney(summary?.won_value ?? 0)}</strong></div>
            <div className="reportInsightRow"><span>Contatos ativos</span><strong>{summary?.total_contacts ?? 0}</strong></div>
            <div className="reportInsightRow"><span>Tempo médio resposta</span><strong>{formatMinutesAsLabel(summary?.avg_first_response_minutes ?? 0)}</strong></div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Tab: Atendimento ───────────────────────────────────────────────────────────
function TabAtendimento({ attendantStats, summary, loading }) {
  const stats = ensureArray(attendantStats);
  const maxConv = Math.max(1, ...stats.map((s) => Number(s.total_conversations || 0)));
  const maxMsg = Math.max(1, ...stats.map((s) => Number(s.total_messages_sent || 0)));

  return (
    <div className="reportTabContent">
      <div className="reportKpiGrid">
        <KpiCard title="Total atendentes" value={stats.length} hint="usuários ativos" tone="default" icon="contact" />
        <KpiCard title="Conversas abertas" value={summary?.open_conversations ?? 0} hint="fila atual" tone="green" icon="chat" />
        <KpiCard title="Finalizadas" value={summary?.finished_conversations ?? 0} hint="ciclo encerrado" tone="blue" icon="check" />
        <KpiCard title="SLA crítico" value={summary?.critical_conversations ?? 0} hint="aguardando > 1h" tone="red" icon="warning" />
      </div>

      <div className="reportTableWrapper">
        <div className="reportBoxHeader">
          <h3>Performance por atendente</h3>
          <span className="reportBoxHint">todos os períodos</span>
        </div>
        {stats.length === 0 ? (
          <EmptyState illustration="team" title="Sem dados de atendentes" text="Atribua conversas a atendentes para visualizar a performance." />
        ) : (
          <table className="reportTable">
            <thead>
              <tr>
                <th>Atendente</th>
                <th>Total conversas</th>
                <th>Abertas</th>
                <th>Finalizadas</th>
                <th>Mensagens enviadas</th>
                <th>Tempo médio 1ª resp.</th>
              </tr>
            </thead>
            <tbody>
              {stats.map((s) => (
                <tr key={s.id}>
                  <td><strong>{s.name}</strong></td>
                  <td>{s.total_conversations || 0}</td>
                  <td><span className={`reportPill ${Number(s.open_conversations || 0) > 0 ? "amber" : "green"}`}>{s.open_conversations || 0}</span></td>
                  <td>{s.finished_conversations || 0}</td>
                  <td>{s.total_messages_sent || 0}</td>
                  <td>{formatMinutesAsLabel(s.avg_first_response_minutes || 0)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="reportChartGrid">
        <div className="reportBox">
          <div className="reportBoxHeader"><h3>Conversas por atendente</h3></div>
          {stats.map((s) => (
            <BarRow key={s.id} label={s.name} value={s.total_conversations || 0} max={maxConv} />
          ))}
        </div>
        <div className="reportBox">
          <div className="reportBoxHeader"><h3>Mensagens enviadas</h3></div>
          {stats.map((s) => (
            <BarRow key={s.id} label={s.name} value={s.total_messages_sent || 0} max={maxMsg} alt />
          ))}
        </div>
      </div>
    </div>
  );
}

// ── Tab: Funil ─────────────────────────────────────────────────────────────────
function TabFunil({ funnelStats, stages: rawStages = [], leads: rawLeads = [], loading }) {
  const data = ensureObject(funnelStats);
  const stages = ensureArray(data.stages);
  const maxTotal = Math.max(1, ...stages.map((s) => Number(s.total_leads || 0)));

  // Build per-stage leads list for the visual Pipeline (kanban)
  const leadsByStage = ensureArray(rawStages).map((stage) => ({
    ...stage,
    items: ensureArray(rawLeads).filter((l) => Number(l.stage_id) === Number(stage.id)),
  }));

  const conversionRate = (() => {
    const totalEntered = stages.reduce((s, st) => s + Number(st.total_leads || 0), 0);
    if (totalEntered === 0) return 0;
    return Math.round(((data.won_leads || 0) / totalEntered) * 100);
  })();

  return (
    <div className="reportTabContent">
      <div className="reportKpiGrid">
        <KpiCard title="Leads ativos" value={data.total_active_leads ?? 0} hint="em andamento no funil" tone="purple" icon="deal" />
        <KpiCard title="Ganhos" value={data.total_won_leads ?? 0} hint="negócios fechados" tone="green" icon="check" />
        <KpiCard title="Perdidos" value={data.total_lost_leads ?? 0} hint="ciclo encerrado sem conversão" tone="red" icon="close" />
        <KpiCard title="Receita gerada" value={formatMoney(data.total_won_value ?? 0)} hint="soma dos ganhos" tone="blue" icon="deal" />
        <KpiCard title="Ticket médio" value={formatMoney(data.avg_deal_value ?? 0)} hint="por negócio ganho" tone="amber" icon="deal" />
        <KpiCard title="Taxa de conversão" value={`${conversionRate}%`} hint="leads ganhos / total" tone="cyan" icon="report" />
      </div>

      <div className="reportChartGrid">
        <div className="reportBox" style={{ gridColumn: "span 2" }}>
          <div className="reportBoxHeader"><h3>Funil de conversão</h3><span className="reportBoxHint">leads por etapa</span></div>
          <div className="funnelChart">
            {stages.map((stage, idx) => {
              const pct = maxTotal > 0 ? Math.round((Number(stage.total_leads || 0) / maxTotal) * 100) : 0;
              const colors = ["#128C7E", "#0891b2", "#7c3aed", "#d97706", "#dc2626", "#16a34a"];
              const color = colors[idx % colors.length];
              return (
                <div key={stage.id} className="funnelStageRow">
                  <div className="funnelStageLabel">
                    <strong>{stage.name}</strong>
                    <div className="funnelStageMeta">
                      <span>{stage.total_leads || 0} lead(s)</span>
                      <span>{formatMoney(stage.total_value || 0)}</span>
                    </div>
                  </div>
                  <div className="funnelBarTrack">
                    <div className="funnelBar" style={{ width: `${pct}%`, background: color }} />
                  </div>
                </div>
              );
            })}
          </div>
          {stages.length === 0 && <EmptyState illustration="funnel" title="Sem etapas configuradas" text="Crie etapas no Funil para visualizar aqui." />}
        </div>

        <div className="reportBox">
          <div className="reportBoxHeader"><h3>Resumo do pipeline</h3></div>
          <div className="reportInsightList">
            <div className="reportInsightRow"><span>Total em funil</span><strong>{stages.reduce((s, st) => s + Number(st.total_leads || 0), 0)}</strong></div>
            <div className="reportInsightRow"><span>Total ganhos</span><strong>{stages.reduce((s, st) => s + Number(st.won || 0), 0)}</strong></div>
            <div className="reportInsightRow"><span>Total perdidos</span><strong>{stages.reduce((s, st) => s + Number(st.lost || 0), 0)}</strong></div>
            <div className="reportInsightRow"><span>Valor total em aberto</span><strong>{formatMoney(stages.reduce((s, st) => s + Number(st.total_value || 0), 0))}</strong></div>
          </div>
        </div>
      </div>

    </div>
  );
}

// ── Tab: Qualidade ─────────────────────────────────────────────────────────────
function TabQualidade({ summary, attendantStats, loading }) {
  const stats = ensureArray(attendantStats);
  const totalConv = Number(summary?.total_conversations || 0);
  const finished = Number(summary?.finished_conversations || 0);
  const resolutionRate = totalConv > 0 ? Math.round((finished / totalConv) * 100) : 0;

  const slaOk = totalConv - Number(summary?.critical_conversations || 0) - Number(summary?.warning_conversations || 0);
  const slaRate = totalConv > 0 ? Math.round((slaOk / totalConv) * 100) : 100;

  const avgResponse = summary?.avg_first_response_minutes ?? 0;
  let responseTone = "green";
  if (avgResponse > 60) responseTone = "red";
  else if (avgResponse > 20) responseTone = "amber";

  return (
    <div className="reportTabContent">
      <div className="reportKpiGrid">
        <KpiCard title="Taxa de resolução" value={`${resolutionRate}%`} hint="conversas finalizadas" tone={resolutionRate >= 70 ? "green" : "amber"} icon="check" />
        <KpiCard title="SLA cumprido" value={`${slaRate}%`} hint="respondidas dentro do prazo" tone={slaRate >= 80 ? "green" : "red"} icon="clock" />
        <KpiCard title="Tempo médio 1ª resp." value={formatMinutesAsLabel(avgResponse)} hint={avgResponse <= 20 ? "excelente" : avgResponse <= 60 ? "aceitável" : "acima do ideal"} tone={responseTone} icon="clock" />
        <KpiCard title="Críticos" value={summary?.critical_conversations ?? 0} hint="aguardando > 60 min" tone="red" icon="bell" />
        <KpiCard title="Em atenção" value={summary?.warning_conversations ?? 0} hint="aguardando 15–60 min" tone="amber" icon="bell" />
        <KpiCard title="Total conversas" value={totalConv} hint="histórico completo" tone="default" icon="chat" />
      </div>

      <div className="reportChartGrid">
        <div className="reportBox">
          <div className="reportBoxHeader"><h3>Indicadores de qualidade</h3></div>
          <div className="reportInsightList">
            <div className="reportInsightRow"><span>Taxa de resolução</span><strong className={resolutionRate >= 70 ? "good" : "warn"}>{resolutionRate}%</strong></div>
            <div className="reportInsightRow"><span>SLA cumprido</span><strong className={slaRate >= 80 ? "good" : "warn"}>{slaRate}%</strong></div>
            <div className="reportInsightRow"><span>Tempo médio resposta</span><strong>{formatMinutesAsLabel(avgResponse)}</strong></div>
            <div className="reportInsightRow"><span>Conversas críticas</span><strong className={Number(summary?.critical_conversations || 0) > 0 ? "danger" : "good"}>{summary?.critical_conversations ?? 0}</strong></div>
            <div className="reportInsightRow"><span>Aguardando retorno</span><strong>{summary?.waiting_conversations ?? 0}</strong></div>
          </div>
        </div>

        <div className="reportBox">
          <div className="reportBoxHeader"><h3>Tempo médio de resposta por atendente</h3></div>
          {stats.length === 0 ? (
            <EmptyState illustration="chart" title="Sem dados" text="Conversas atribuídas aparecerão aqui." />
          ) : (
            stats.map((s) => {
              const mins = s.avg_first_response_minutes || 0;
              const tone = mins <= 15 ? "var(--green)" : mins <= 60 ? "var(--amber)" : "var(--red)";
              return (
                <div key={s.id} className="reportBarRow">
                  <div className="reportBarLabel">
                    <strong>{s.name}</strong>
                    <span style={{ color: tone }}>{formatMinutesAsLabel(mins)}</span>
                  </div>
                  <div className="reportBarTrack">
                    <div style={{ width: `${Math.min(100, Math.round((mins / 120) * 100))}%`, background: tone }} />
                  </div>
                </div>
              );
            })
          )}
        </div>

        <div className="reportBox emphasis">
          <h3>SLA — critérios</h3>
          <p>O tempo de resposta é calculado desde a última mensagem do cliente até a primeira resposta do atendente.</p>
          <div className="reportInsightList" style={{ marginTop: 12 }}>
            <div className="reportInsightRow">
              <span><span className="reportPill green">Verde</span> &lt; 15 min</span>
              <strong>Excelente</strong>
            </div>
            <div className="reportInsightRow">
              <span><span className="reportPill amber">Amarelo</span> 15–60 min</span>
              <strong>Atenção</strong>
            </div>
            <div className="reportInsightRow">
              <span><span className="reportPill red">&gt; 60 min</span></span>
              <strong>Crítico</strong>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Tab: Finalização ──────────────────────────────────────────────────────────
const RESOLUTION_META = {
  resolved:  { label: "Resolvido",      color: "var(--success)", tone: "green"  },
  no_return: { label: "Sem retorno",    color: "var(--warning)", tone: "amber"  },
  forwarded: { label: "Encaminhado",    color: "var(--info)",    tone: "blue"   },
  spam:      { label: "Spam / Inválido",color: "var(--danger)",  tone: "red"    },
  unknown:   { label: "Sem tipo",       color: "var(--text-4)",  tone: "default"},
};

function TabFinalizacao({ finData, loading, period, onPeriodChange }) {
  const data = ensureObject(finData);
  const total = Number(data.total || 0);
  const byResolution = ensureArray(data.by_resolution);
  const topTags      = ensureArray(data.top_tags);
  const byAgent      = ensureArray(data.by_agent);
  const recent       = ensureArray(data.recent);
  const timeline     = ensureArray(data.timeline);

  const maxAgent = Math.max(1, ...byAgent.map((a) => Number(a.total || 0)));
  const maxTag   = Math.max(1, ...topTags.map((t) => Number(t.count || 0)));

  // Resolution % helper
  function pct(type) {
    const row = byResolution.find((r) => r.resolution_type === type);
    const count = Number(row?.total || 0);
    return total > 0 ? Math.round((count / total) * 100) : 0;
  }
  function cnt(type) {
    const row = byResolution.find((r) => r.resolution_type === type);
    return Number(row?.total || 0);
  }

  function formatFinDate(dt) {
    if (!dt) return "—";
    const d = new Date(dt);
    return d.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" }) +
      " " + d.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
  }

  if (loading && total === 0) {
    return <div className="reportLoading"><EmptyState icon="clock" title="Calculando…" /></div>;
  }

  return (
    <div className="reportTabContent">
      {/* Period filter */}
      <div className="finReport-periodBar">
        <span className="finReport-periodLabel">Período:</span>
        {[7, 14, 30, 60, 90].map((d) => (
          <button
            key={d}
            type="button"
            className={`finReport-periodBtn${period === d ? " is-active" : ""}`}
            onClick={() => onPeriodChange(d)}
          >
            {d === 7 ? "7 dias" : d === 14 ? "14 dias" : d === 30 ? "30 dias" : d === 60 ? "2 meses" : "3 meses"}
          </button>
        ))}
      </div>

      {/* KPIs */}
      <div className="reportKpiGrid">
        <KpiCard title="Finalizadas" value={total} hint={`últimos ${period} dias`} tone="blue" icon="check" />
        <KpiCard title="Resolvido" value={`${pct("resolved")}%`} hint={`${cnt("resolved")} atendimentos`} tone="green" icon="checkCircle" />
        <KpiCard title="Sem retorno" value={`${pct("no_return")}%`} hint={`${cnt("no_return")} atendimentos`} tone="amber" icon="clock" />
        <KpiCard title="Encaminhado" value={`${pct("forwarded")}%`} hint={`${cnt("forwarded")} transferências`} tone="blue" icon="send" />
        <KpiCard title="Spam / Inválido" value={`${pct("spam")}%`} hint={`${cnt("spam")} descartados`} tone="red" icon="warning" />
        <KpiCard title="Tags registradas" value={topTags.reduce((s, t) => s + Number(t.count || 0), 0)} hint={`${topTags.length} tags distintas`} tone="default" icon="tag" />
      </div>

      <div className="reportChartGrid">
        {/* Resolução breakdown */}
        <div className="reportBox">
          <div className="reportBoxHeader"><h3>Tipo de resolução</h3><span className="reportBoxHint">distribuição do período</span></div>
          {byResolution.length === 0 ? (
            <EmptyState illustration="chart" title="Sem dados" text="Finalize conversas usando o modal para ver aqui." />
          ) : (
            <>
              {byResolution.map((r) => {
                const meta = RESOLUTION_META[r.resolution_type] || RESOLUTION_META.unknown;
                const rowPct = total > 0 ? Math.round((Number(r.total) / total) * 100) : 0;
                return (
                  <div key={r.resolution_type} className="reportBarRow">
                    <div className="reportBarLabel">
                      <strong>{meta.label}</strong>
                      <span>{r.total} ({rowPct}%)</span>
                    </div>
                    <div className="reportBarTrack">
                      <div style={{ width: `${rowPct}%`, background: meta.color }} />
                    </div>
                  </div>
                );
              })}
              {/* Visual donut-like summary */}
              <div className="finReport-resCircles">
                {byResolution.map((r) => {
                  const meta = RESOLUTION_META[r.resolution_type] || RESOLUTION_META.unknown;
                  const rowPct = total > 0 ? Math.round((Number(r.total) / total) * 100) : 0;
                  return (
                    <div key={r.resolution_type} className="finReport-resCircle">
                      <div className="finReport-resCircle-ring" style={{ "--clr": meta.color, "--pct": rowPct }}>
                        <span>{rowPct}%</span>
                      </div>
                      <small>{meta.label}</small>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>

        {/* Top Tags */}
        <div className="reportBox">
          <div className="reportBoxHeader"><h3>Tags mais usadas</h3><span className="reportBoxHint">nas finalizações</span></div>
          {topTags.length === 0 ? (
            <EmptyState illustration="tag" title="Sem tags ainda" text="Tags adicionadas no modal de finalização aparecem aqui." />
          ) : (
            <>
              {topTags.map((t) => (
                <div key={t.tag} className="reportBarRow">
                  <div className="reportBarLabel">
                    <strong className="finReport-tagPill">{t.tag}</strong>
                    <span>{t.count}×</span>
                  </div>
                  <div className="reportBarTrack alt">
                    <div style={{ width: `${Math.round((t.count / maxTag) * 100)}%` }} />
                  </div>
                </div>
              ))}
            </>
          )}
        </div>

        {/* Timeline */}
        <div className="reportBox" style={{ gridColumn: "span 2" }}>
          <div className="reportBoxHeader"><h3>Finalizações por dia</h3><span className="reportBoxHint">últimos {period} dias</span></div>
          <MiniChart data={timeline} color="var(--brand-500)" days={period} />
          <div className="reportChartLegend">
            <span className="legendDot" style={{ background: "var(--brand-500)" }} />Finalizadas
          </div>
        </div>
      </div>

      {/* Por atendente */}
      {byAgent.length > 0 && (
        <div className="reportTableWrapper" style={{ marginTop: 16 }}>
          <div className="reportBoxHeader"><h3>Finalizações por atendente</h3></div>
          <table className="reportTable">
            <thead>
              <tr>
                <th>Atendente</th>
                <th>Total</th>
                <th>Resolvido</th>
                <th>Sem retorno</th>
                <th>Encaminhado</th>
                <th>Spam</th>
                <th>Taxa resolução</th>
              </tr>
            </thead>
            <tbody>
              {byAgent.map((a) => {
                const agTotal = Number(a.total || 0);
                const agResolved = Number(a.resolved || 0);
                const agRate = agTotal > 0 ? Math.round((agResolved / agTotal) * 100) : 0;
                return (
                  <tr key={a.id}>
                    <td><strong>{a.name}</strong></td>
                    <td>{agTotal}</td>
                    <td><span className="reportPill green">{a.resolved || 0}</span></td>
                    <td><span className="reportPill amber">{a.no_return || 0}</span></td>
                    <td><span className="reportPill blue">{a.forwarded || 0}</span></td>
                    <td><span className="reportPill red">{a.spam || 0}</span></td>
                    <td>
                      <div className="finReport-agentBar">
                        <div style={{ width: `${agRate}%` }} />
                        <span>{agRate}%</span>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Últimas finalizações */}
      {recent.length > 0 && (
        <div className="reportTableWrapper" style={{ marginTop: 16 }}>
          <div className="reportBoxHeader"><h3>Últimas finalizações</h3><span className="reportBoxHint">com resumo do atendimento</span></div>
          <div className="finReport-recentList">
            {recent.map((r) => {
              const meta = RESOLUTION_META[r.resolution_type] || RESOLUTION_META.unknown;
              const tags = String(r.finalization_tags || "").split(",").map((t) => t.trim()).filter(Boolean);
              return (
                <div key={r.id} className="finReport-recentItem">
                  <div className="finReport-recentLeft">
                    <div className="finReport-recentName">{r.contact_name || "—"}</div>
                    {r.finalization_summary && <div className="finReport-recentSummary">"{r.finalization_summary}"</div>}
                    {tags.length > 0 && (
                      <div className="finReport-recentTags">
                        {tags.map((t) => <span key={t} className="finReport-recentTag">{t}</span>)}
                      </div>
                    )}
                  </div>
                  <div className="finReport-recentRight">
                    <span className="finReport-resBadge" style={{ color: meta.color, borderColor: meta.color + "44", background: meta.color + "11" }}>
                      {meta.label}
                    </span>
                    <small>{r.agent_name ? `por ${r.agent_name}` : ""}</small>
                    <small className="finReport-recentDate">{formatFinDate(r.finalized_at)}</small>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {total === 0 && !loading && (
        <EmptyState
          illustration="chart"
          title="Nenhuma conversa finalizada no período"
          text="Use o botão Finalizar nas conversas para registrar o tipo de resolução, tags e resumo."
        />
      )}
    </div>
  );
}

// ── Tab: CSAT ──────────────────────────────────────────────────────────────────
function TabCsat({ csatData, loading }) {
  if (loading) return <div className="reportLoading"><EmptyState icon="clock" title="Carregando…" /></div>;
  if (!csatData || csatData.total_ratings === 0) {
    return (
      <div className="reportTabContent">
        <EmptyState illustration="star" title="Sem avaliações ainda" text="As avaliações aparecem aqui quando os clientes respondem ao CSAT após o encerramento do atendimento." />
      </div>
    );
  }

  const stars = [5, 4, 3, 2, 1];
  const maxCount = Math.max(1, ...stars.map((s) => csatData.distribution?.[s] || 0));

  const npsScore = csatData.nps_score ?? 0;
  const npsTone = npsScore >= 50 ? "green" : npsScore >= 0 ? "amber" : "red";

  return (
    <div className="reportTabContent">
      <div className="reportKpiGrid">
        <KpiCard title="Nota média" value={(csatData.avg_rating || 0).toFixed(1)} hint={`${csatData.total_ratings} avaliações`} tone={csatData.avg_rating >= 4 ? "green" : csatData.avg_rating >= 3 ? "amber" : "red"} icon="star" />
        <KpiCard title="Score NPS" value={npsScore} hint="promotores – detratores" tone={npsTone} icon="report" />
        <KpiCard title="Total avaliações" value={csatData.total_ratings || 0} hint="últimos 30 dias" tone="default" icon="chat" />
        <KpiCard title="5 estrelas" value={csatData.distribution?.[5] || 0} hint="excelente" tone="green" icon="star" />
      </div>

      <div className="reportChartGrid">
        <div className="reportBox">
          <div className="reportBoxHeader"><h3>Distribuição por nota</h3></div>
          {stars.map((s) => {
            const count = csatData.distribution?.[s] || 0;
            const pct = Math.round((count / maxCount) * 100);
            return (
              <div key={s} className="reportBarRow">
                <div className="reportBarLabel">
                  <strong>{"★".repeat(s)}</strong>
                  <span>{count}</span>
                </div>
                <div className="reportBarTrack">
                  <div style={{ width: `${pct}%`, background: s >= 4 ? "var(--accent)" : s === 3 ? "#f59e0b" : "#ef4444" }} />
                </div>
              </div>
            );
          })}
        </div>

        {csatData.recent_comments?.length > 0 && (
          <div className="reportBox">
            <div className="reportBoxHeader"><h3>Comentários recentes</h3></div>
            <div className="csatCommentList">
              {csatData.recent_comments.map((c, i) => (
                <div key={i} className="csatCommentItem">
                  <div className="csatCommentRating">{"★".repeat(c.rating)}<span className="csatCommentStarsEmpty">{"★".repeat(5 - c.rating)}</span></div>
                  <p>{c.comment}</p>
                  {c.contact_name && <small>{c.contact_name}</small>}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Main ReportsPage ───────────────────────────────────────────────────────────
function ReportsPage({ api, initialTab, clearInitialTab }) {
  const [tab, setTab] = useState(initialTab || "geral");

  // Deep-link: sidebar pode abrir uma sub-tab específica
  useEffect(() => {
    if (initialTab) {
      setTab(initialTab);
      clearInitialTab?.();
    }
  }, [initialTab]); // eslint-disable-line
  const [summary, setSummary] = useState(null);
  const [timeline, setTimeline] = useState(null);
  const [attendantStats, setAttendantStats] = useState([]);
  const [funnelStats, setFunnelStats] = useState(null);
  const [funnelStages, setFunnelStages] = useState([]);
  const [funnelLeads, setFunnelLeads] = useState([]);
  const [csatData, setCsatData] = useState(null);
  const [finData, setFinData] = useState(null);
  const [finPeriod, setFinPeriod] = useState(30);
  const [loading, setLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(null);

  async function loadAll(period = finPeriod) {
    setLoading(true);
    try {
      const [summaryRes, timelineRes, attendantRes, funnelRes, stagesRes, leadsRes, csatRes, finRes] = await Promise.all([
        api.get("/reports/summary"),
        api.get("/reports/timeline?days=30"),
        api.get("/reports/attendant-stats"),
        api.get("/reports/funnel-stats"),
        api.get("/funnel/stages"),
        api.get("/leads"),
        api.get("/reports/csat?days=30").catch(() => ({ data: null })),
        api.get(`/reports/finalization?days=${period}`).catch(() => ({ data: null })),
      ]);
      setSummary(ensureObject(summaryRes.data));
      setTimeline(ensureObject(timelineRes.data));
      setAttendantStats(ensureArray(attendantRes.data));
      setFunnelStats(ensureObject(funnelRes.data));
      setFunnelStages(ensureArray(stagesRes.data));
      setFunnelLeads(ensureArray(leadsRes.data));
      setCsatData(csatRes.data);
      setFinData(finRes.data);
      setLastRefresh(new Date());
    } catch {}
    setLoading(false);
  }

  useEffect(() => { loadAll(); }, []); // eslint-disable-line

  async function handleFinPeriodChange(days) {
    setFinPeriod(days);
    setLoading(true);
    try {
      const res = await api.get(`/reports/finalization?days=${days}`);
      setFinData(res.data);
    } catch {}
    setLoading(false);
  }

  const TABS = [
    { id: "geral", label: "Geral", icon: "home" },
    { id: "atendimento", label: "Atendimento", icon: "chat" },
    { id: "funil", label: "Funil", icon: "deal" },
    { id: "qualidade", label: "Qualidade", icon: "report" },
    { id: "finalizacao", label: "Finalização", icon: "checkCircle" },
    { id: "csat", label: "Avaliações", icon: "star" },
  ];

  return (
    <div className="reportsPage">
      <div className="reportsTopBar">
        <div className="reportsTitle">
          <h2>Relatórios</h2>
          <small>{lastRefresh ? `Atualizado às ${lastRefresh.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}` : "Carregando…"}</small>
        </div>
        <button className="ghostButton" onClick={loadAll} disabled={loading}>
          <Icon name="refresh" size={14} /> {loading ? "Atualizando…" : "Atualizar"}
        </button>
      </div>

      <div className="reportsTabs">
        {TABS.map((t) => (
          <button
            key={t.id}
            className={`reportsTab${tab === t.id ? " active" : ""}`}
            onClick={() => setTab(t.id)}
          >
            <Icon name={t.icon} size={14} />
            {t.label}
          </button>
        ))}
      </div>

      {loading && !summary ? (
        <div className="reportLoading">
          <EmptyState icon="clock" title="Carregando relatórios" text="Calculando indicadores e buscando dados históricos…" />
        </div>
      ) : (
        <>
          {tab === "geral" && <TabGeral summary={summary} timeline={timeline} loading={loading} />}
          {tab === "atendimento" && <TabAtendimento attendantStats={attendantStats} summary={summary} loading={loading} />}
          {tab === "funil" && <TabFunil funnelStats={funnelStats} stages={funnelStages} leads={funnelLeads} loading={loading} />}
          {tab === "qualidade" && <TabQualidade summary={summary} attendantStats={attendantStats} loading={loading} />}
          {tab === "finalizacao" && <TabFinalizacao finData={finData} loading={loading} period={finPeriod} onPeriodChange={handleFinPeriodChange} />}
          {tab === "csat" && <TabCsat csatData={csatData} loading={loading} />}
        </>
      )}
    </div>
  );
}

export default ReportsPage;
